/**
 * Svelte store for user settings
 */
import { writable, get } from 'svelte/store';
import { locale as i18nLocale } from 'svelte-i18n';
import type { UserSettings, DateFormatType, TimeFormatType } from '../settings';
import { defaultSettings } from '../settings';
import { normalizeLocale } from '../i18n';
import { api } from '../api';
import { ApiError } from '../api/errorHandler';

export const LOCALE_CACHE_KEY = 'user_locale';

/**
 * Create a settings store with load/update capabilities
 */
function createSettingsStore() {
  const { subscribe, set, update } = writable<UserSettings>(defaultSettings);

  let loaded = false;

  return {
    subscribe,

    /**
     * Check if settings have been loaded from the server
     */
    get isLoaded() {
      return loaded;
    },

    /**
     * Load settings from the server
     */
    async load(): Promise<void> {
      try {
        const settings = await api.settings.getUserSettings();
        set(settings);
        loaded = true;

        // Sync i18n with user's locale preference
        if (settings.locale) {
          const normalized = normalizeLocale(settings.locale);
          i18nLocale.set(normalized);
          localStorage.setItem(LOCALE_CACHE_KEY, normalized);
          if (typeof document !== 'undefined') {
            document.documentElement.lang = normalized;
          }
        }
      } catch (error) {
        console.error('Failed to load user settings:', error);
        // 403 (EmailWhitelist) means the account isn't provisioned — surface it so
        // App.svelte can show the dedicated access-denied page instead of silently
        // keeping default settings (Spec 015 E5).
        if (error instanceof ApiError && error.status === 403) throw error;
        // Keep default settings on other errors
      }
    },

    /**
     * Update settings on the server and in the store
     */
    async updateSettings(newSettings: Partial<UserSettings>): Promise<void> {
      try {
        await api.settings.updateUserSettings(newSettings);
        update((current) => ({
          ...current,
          ...newSettings,
        }));
      } catch (error) {
        console.error('Failed to update user settings:', error);
        throw error;
      }
    },

    /**
     * Update locale and sync with i18n
     */
    async updateLocale(locale: string): Promise<void> {
      const normalized = normalizeLocale(locale);
      // Update i18n and cache immediately for responsive UI
      i18nLocale.set(normalized);
      localStorage.setItem(LOCALE_CACHE_KEY, normalized);
      if (typeof document !== 'undefined') {
        document.documentElement.lang = normalized;
      }
      // Persist to backend and store
      return this.updateSettings({ locale });
    },

    /**
     * Update date format
     */
    async updateDateFormat(dateFormat: DateFormatType): Promise<void> {
      return this.updateSettings({ dateFormat });
    },

    /**
     * Update time format
     */
    async updateTimeFormat(timeFormat: TimeFormatType): Promise<void> {
      return this.updateSettings({ timeFormat });
    },

    /**
     * Get current settings (synchronous)
     */
    getCurrent(): UserSettings {
      return get({ subscribe });
    },

    /**
     * Reset to defaults (client-side only)
     */
    reset(): void {
      set(defaultSettings);
      loaded = false;
    },
  };
}

export const userSettings = createSettingsStore();
